import { Tooltip } from '@brightdrop/bd-ui';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { type ReactNode, useState } from 'react';

interface AdaptiveTooltipDrawerProps {
  isMobile?: boolean;
  ariaLabel: string;
  icon: ReactNode;
  content: ReactNode | string;
  drawerTitle?: ReactNode;
  dataTestId?: string;
  className?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

const AdaptiveTooltipDrawer = ({
  isMobile = false,
  ariaLabel,
  icon,
  content,
  drawerTitle,
  dataTestId,
  className,
  placement = 'bottom',
}: AdaptiveTooltipDrawerProps): JSX.Element => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleOpenDrawer = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => setDrawerOpen(false);

  const contentNode =
    typeof content === 'string' ? (
      <Typography variant="small_body_regular">{content}</Typography>
    ) : (
      content
    );

  const iconButton = (
    <IconButton
      size="small"
      className={className}
      sx={{ padding: 0, fontSize: 'inherit' }}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      onClick={isMobile ? handleOpenDrawer : undefined}
    >
      {icon}
    </IconButton>
  );

  if (isMobile) {
    return (
      <>
        {iconButton}
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={handleCloseDrawer}
          // The drawer portals out of the DOM but not the React tree; keep its clicks off ancestor handlers.
          onClick={(e) => e.stopPropagation()}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: 'common.white',
                borderTopLeftRadius: 2,
                borderTopRightRadius: 2,
                height: 'auto',
                maxHeight: '80vh',
              },
            },
          }}
        >
          <Stack spacing={1} sx={{ p: 2, pb: 3 }}>
            <Box
              aria-hidden
              sx={{
                alignSelf: 'center',
                width: 80,
                height: 4,
                borderRadius: 0.5,
                backgroundColor: 'grey.400',
                mb: 0.5,
              }}
            />
            {drawerTitle ? (
              <Typography variant="small_body_medium">{drawerTitle}</Typography>
            ) : null}
            {contentNode}
          </Stack>
        </Drawer>
      </>
    );
  }

  return (
    <Tooltip
      content={typeof content === 'string' ? content : ''}
      customChildren={typeof content === 'string' ? undefined : content}
      placement={placement}
      showArrow
      trigger={iconButton}
    />
  );
};

export default AdaptiveTooltipDrawer;
